import React, { useEffect, useRef } from 'react';

/**
 * Cloudflare Turnstile widget'ı.
 *
 * Siber Güvenlik Başkanlığı E-17394444-951.01.09-10044 sayılı yazısı,
 * Teknik Ek md. 1.4 (giriş ve şifre sıfırlama formlarında bot engelleme)
 * gereği eklenmiştir.
 *
 * Site key .env dosyasında REACT_APP_TURNSTILE_SITE_KEY olarak tanımlanır.
 * Tanımlı değilse widget hiç render edilmez ve giriş akışı bozulmaz.
 */

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let scriptPromise = null;

function loadTurnstileScript() {
	if (window.turnstile) return Promise.resolve(window.turnstile);
	if (scriptPromise) return scriptPromise;

	scriptPromise = new Promise((resolve, reject) => {
		const script = document.createElement('script');
		script.src = SCRIPT_SRC;
		script.async = true;
		script.defer = true;
		script.onload = () => resolve(window.turnstile);
		script.onerror = () => reject(new Error('Turnstile yüklenemedi'));
		document.head.appendChild(script);
	});
	return scriptPromise;
}

/**
 * onStatus, çağıran tarafın giriş düğmesini doğru yönetmesi içindir:
 *   'disabled' - site key yok, widget hiç çizilmiyor  -> düğme AÇIK kalmalı
 *   'loading'  - widget doğrulama yapıyor             -> düğme kapalı
 *   'ready'    - token alındı                          -> düğme açık
 *   'error'    - Cloudflare'a ulaşılamadı / süre doldu  -> düğme AÇIK kalmalı
 *
 * 'error' ve 'disabled' durumlarında düğmenin açık kalması KASITLI: aksi hâlde
 * Cloudflare erişilemez olduğunda panele kimse giremezdi. Bu durumlarda koruma
 * sunucudaki kaba kuvvet kilitlemesiyle sürer.
 */
const Turnstile = ({ onToken, onStatus, resetKey = 0, theme = 'auto' }) => {
	const containerRef = useRef(null);
	const widgetIdRef = useRef(null);

	const report = (durum) => { if (onStatus) onStatus(durum); };

	useEffect(() => {
		if (!SITE_KEY) {
			report('disabled');
			return undefined;
		}
		report('loading');

		// Güvenlik ağı: widget ne token ne hata döndürmezse (beklenmedik bir
		// durum, ağ takılması vb.) giriş düğmesi sonsuza kadar kapalı kalmasın.
		// 15 saniye sonra 'error' sayılır; koruma sunucudaki kilitlemeyle sürer.
		const zamanAsimi = setTimeout(() => {
			if (!cozuldu) report('error');
		}, 15000);

		let cancelled = false;
		let cozuldu = false;

		loadTurnstileScript()
			.then((turnstile) => {
				if (cancelled || !containerRef.current || !turnstile) return;
				widgetIdRef.current = turnstile.render(containerRef.current, {
					sitekey: SITE_KEY,
					theme,
					callback: (token) => { cozuldu = true; onToken(token); report('ready'); },
					'expired-callback': () => { onToken(''); report('error'); },  // süresi dolduysa yeniden çözülür
					'error-callback': () => { cozuldu = true; onToken(''); report('error'); },
				});
			})
			.catch(() => {
				// Turnstile'a ulaşılamazsa giriş akışı engellenmez;
				// sunucu tarafında brute force koruması devrede kalır.
				cozuldu = true;
				onToken('');
				report('error');
			});

		return () => {
			cancelled = true;
			clearTimeout(zamanAsimi);
			if (widgetIdRef.current && window.turnstile) {
				window.turnstile.remove(widgetIdRef.current);
				widgetIdRef.current = null;
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/**
	 * Başarısız giriş sonrası token tek kullanımlık olduğu için sıfırlanır.
	 * resetKey her artırıldığında widget yenilenir; çağıran taraf statik bir
	 * metoda erişmek zorunda kalmaz.
	 */
	useEffect(() => {
		if (resetKey && widgetIdRef.current && window.turnstile) {
			window.turnstile.reset(widgetIdRef.current);
			onToken('');
			report('loading');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [resetKey]);

	/** Başarısız giriş sonrası token tek kullanımlık olduğu için sıfırlanır. */
	useEffect(() => {
		Turnstile.reset = () => {
			if (widgetIdRef.current && window.turnstile) {
				window.turnstile.reset(widgetIdRef.current);
			}
		};
	}, []);

	if (!SITE_KEY) return null;

	return <div ref={containerRef} className='d-flex justify-content-center mb-3' />;
};

export const isTurnstileEnabled = () => Boolean(SITE_KEY);

export default Turnstile;
